import React, { useState } from 'react';
import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import '../../styles/SavedReports.css';

const SavedReports = ({ savedReports, onLoadReport, onRefresh, currentUser }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, mine, default
  const [searchTerm, setSearchTerm] = useState('');

  const handleDeleteReport = async (reportId, reportName) => {
    const confirmed = await confirm({
      title: 'Delete Report',
      message: `Are you sure you want to delete "${reportName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'savedReports', reportId));
      toast.success('Report deleted.');
      onRefresh();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Error deleting report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDefault = async (reportId, currentDefault) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'savedReports', reportId), {
        isDefault: !currentDefault,
        updatedAt: new Date(),
        updatedBy: currentUser.uid
      });
      onRefresh();
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Error updating report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredReports = () => {
    let filtered = savedReports;

    // Apply filter
    switch (filter) {
      case 'mine':
        filtered = filtered.filter(report => report.createdBy === currentUser.uid);
        break;
      case 'default':
        filtered = filtered.filter(report => report.isDefault);
        break;
      default:
        // Show all reports
        break;
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.description && report.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  };

  const getDataSourceLabel = (dataSource) => {
    const labels = {
      'candidates': 'Candidates',
      'actionReports': 'Action Reports',
      'courseOfStudy': 'Course of Study',
      'combined': 'Combined Data'
    };
    return labels[dataSource] || dataSource;
  };

  const canEditReport = (report) => {
    return currentUser.role === 'Admin' || report.createdBy === currentUser.uid;
  };

  const filteredReports = getFilteredReports();

  return (
    <div className="saved-reports">
      <div className="saved-reports-header">
        <h2>Saved Reports</h2>
        <div className="reports-controls">
          <div className="filter-controls">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Reports</option>
              <option value="mine">My Reports</option>
              <option value="default">Default Reports</option>
            </select>
            
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <button
            className="btn btn-secondary"
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="reports-grid">
        {filteredReports.length === 0 ? (
          <div className="no-reports">
            <p>No saved reports found.</p>
            {filter !== 'all' && (
              <button
                className="btn btn-secondary"
                onClick={() => setFilter('all')}
              >
                Show All Reports
              </button>
            )}
          </div>
        ) : (
          filteredReports.map(report => (
            <div key={report.id} className="report-card">
              <div className="report-card-header">
                <h3 className="report-title">
                  {report.name}
                  {report.isDefault && <span className="default-badge">Default</span>}
                </h3>
                <div className="report-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => onLoadReport(report.id)}
                    disabled={loading}
                  >
                    Load
                  </button>
                  
                  {canEditReport(report) && (
                    <>
                      <button
                        className={`btn btn-sm ${report.isDefault ? 'btn-warning' : 'btn-secondary'}`}
                        onClick={() => handleToggleDefault(report.id, report.isDefault)}
                        disabled={loading}
                        title={report.isDefault ? 'Remove from defaults' : 'Set as default'}
                      >
                        {report.isDefault ? '★' : '☆'}
                      </button>
                      
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteReport(report.id, report.name)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="report-card-body">
                {report.description && (
                  <p className="report-description">{report.description}</p>
                )}
                
                <div className="report-details">
                  <div className="detail-item">
                    <span className="detail-label">Data Source:</span>
                    <span className="detail-value">{getDataSourceLabel(report.config?.dataSource)}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Columns:</span>
                    <span className="detail-value">{report.config?.columns?.length || 0}</span>
                  </div>
                  
                  <div className="detail-item">
                    <span className="detail-label">Filters:</span>
                    <span className="detail-value">{report.config?.filters?.length || 0}</span>
                  </div>
                  
                  {report.config?.groupBy && (
                    <div className="detail-item">
                      <span className="detail-label">Grouped by:</span>
                      <span className="detail-value">{report.config.groupBy}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="report-card-footer">
                <div className="report-meta">
                  <span className="created-by">
                    Created by: {report.createdBy === currentUser.uid ? 'You' : 'Other user'}
                  </span>
                  <span className="created-date">
                    {new Date(report.createdAt?.toDate?.() || report.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {filteredReports.length > 0 && (
        <div className="reports-summary">
          <p>
            Showing {filteredReports.length} of {savedReports.length} saved reports
          </p>
        </div>
      )}
    </div>
  );
};

export default SavedReports;
